import { createRouter, createWebHistory } from "vue-router"
import store from "@/store"
import {
  USER_GET,
  AUTH_ERROR,
  AUTH_SUCCESS,
  AUTH_SET_HIJACK,
  APPLICATION_TEMPLATE_CHECK_AVAILABLE
} from "@/store/types"

import HomeRoutes from "./home"
import ApplyRoutes from "./apply"
import DashboardRoutes from "./dashboard"
import ErrorRoutes from "./error"
import RequestPaymentRoutes from "./request_payment"
import FlowRoutes from "./flow"
import SigningRoutes from "./signing"

import seoPages from "@/constants/seoPages"

const routes = [
  HomeRoutes,
  DashboardRoutes,
  ApplyRoutes,
  ErrorRoutes,
  RequestPaymentRoutes,
  FlowRoutes,
  SigningRoutes,
  {
    path: "/:catchAll(.*)",
    name: "404",
    component: () => import("@/views/error/NotFound.vue"),
    beforeEnter: async (to, _, next) => {
      // If routed directly to 404 due to error
      if (to.params.direct === "true") {
        next()
        return
      } else {
        const params = to.params.catchAll

        const queryParams = to.query

        if (params) {
          const slug = params.split("/")[0]

          try {
            const resp = await store.dispatch(APPLICATION_TEMPLATE_CHECK_AVAILABLE, { slug })

            if (!resp.data.available) {
              const router = {
                name: "apply_form",
                params: { slug }
              }

              if (queryParams && Object.keys(queryParams).length) {
                router.query = {
                  ...queryParams
                }
              }

              next(router)
              return
            }
          } catch (err) {
            console.log(err)
          }
        }
        next()
      }
    }
  }
]

// Generate SEO Page Routes
const homeBaseRoute = routes.find((r) => r.name === "home_base")
const homeLandingRoute = homeBaseRoute.children.find((r) => r.name === "home_landing")

const seoPageRoutes = seoPages.map((p) => {
  return {
    ...homeLandingRoute,
    ...p,
    name: `home_${p.name}`
  }
})

homeBaseRoute.children = [...homeBaseRoute.children, ...seoPageRoutes]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      //Does not work but it's the vue way
      return { el: to.hash }
    }

    if (savedPosition) {
      return savedPosition
    }
    //Does not work but it's the vue way
    return { top: 0, behavior: "smooth" }
  }
})

/* Route Guard */
router.beforeEach(async (to, from, next) => {
  if (to.query.hijack) {
    const token = { access: to.query.hijack }
    // localStorage.setItem("token", token)
    store.commit(AUTH_SUCCESS, token)
    store.commit(AUTH_SET_HIJACK, true)
  }

  if (to.query.token) {
    const token = { access: to.query.token }
    store.commit(AUTH_SUCCESS, token)
  }

  if (from.name && from.name.includes("flow") && !to.params.flowCheck) {
    const currentStep = from.meta.steps.filter((step) => step.routes.includes(from.name))

    if (to.name.includes("flow") || to.name === from.meta.redirectRoute) {
      next({ name: to.name, params: { flowCheck: true } })
      return
    }

    if (currentStep.length) {
      const currentStepIndex = from.meta.steps.indexOf(currentStep[0])

      let nextRoute

      // If currentStep is the last route in the step
      if (currentStep[0].routes.indexOf(from.name) === currentStep[0].routes.length - 1) {
        //If currentStep is the last step
        if (currentStepIndex === from.meta.steps.length - 1) {
          nextRoute = from.meta.redirectRoute
        } else {
          nextRoute = from.meta.steps[currentStepIndex + 1].routes[0]
        }
      } else {
        nextRoute = currentStep[0].routes[currentStep[0].routes.indexOf(from.name) + 1]
      }
      next({ name: nextRoute, params: { flowCheck: true } })
      return
    }
  }

  if (
    to.matched.some((route) => route.meta.requireAuth) &&
    !store.getters.isAuthenticated &&
    !to.query.hijack &&
    !to.params.token &&
    !to.query.backend_token
  ) {
    console.log("not isAuthenticated")
    next({ name: "home_login" })
  } else if (
    !to.matched.some((route) => route.meta.requireAuth) ||
    to.params.token ||
    to.query.backend_token
  ) {
    next()
  } else {
    if (!store.getters.getCurrentUser) {
      try {
        await store.dispatch(USER_GET)
        // Redirect to classic if edge_enabled = false
        if (
          process.env.VUE_APP_CLASSIC_BASE_URI &&
          store.getters.getCurrentUser.landlord &&
          !store.getters.getCurrentUser.landlord.edge_enabled
        ) {
          location.href = `${process.env.VUE_APP_CLASSIC_BASE_URI}/login`
        }
        next()
      } catch (err) {
        console.log("getCurrentUser login fail")
        store.commit(AUTH_ERROR, err)
        next({ name: "home_login" })
      }
    } else {
      next()
    }
  }
})

router.onError((error, to) => {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  ) {
    console.log(error)
    window.location = to.fullPath
  }
})

export default router
